import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { getBlogPostBySlug } from "@/lib/case-studies.functions";
import { seo } from "@/lib/seo";
import { breadcrumb, article, faqPage, portableTextWordCount } from "@/lib/structured-data";
import { schemaImages, FALLBACK_SCHEMA_IMAGES } from "@/lib/sanity-image";
import { PortableText } from "@/lib/portable-text";
import { NotFoundPage } from "@/components/not-found-page";
import { SITE_URL } from "@/lib/seo-constants";
import { urlFor, type BlogPost } from "@/lib/sanity";

export const Route = createFileRoute("/blog/$slug")({
  loader: async ({ params }): Promise<BlogPost> => {
    const post = await getBlogPostBySlug({ data: { slug: params.slug } });
    if (!post) throw notFound();
    return post;
  },
  head: ({ loaderData, params }) => {
    const path = `/blog/${params.slug}`;
    if (!loaderData) {
      return seo({
        title: "Post not found",
        description: "The blog post you're looking for doesn't exist.",
        path,
        noindex: true,
      });
    }
    const post = loaderData as BlogPost;
    const hasBody = Array.isArray(post.body) && post.body.length > 0;
    const slug = (post.slug?.current ?? "").toString();
    const crumbs = [
      { name: "Home", url: "/" },
      { name: "Blog", url: "/blog" },
      { name: post.title, url: `/blog/${slug}` },
    ];
    // The GROQ projection dereferences the asset (`coverImage{ asset->{ url } }`),
    // so this is already the `{ asset: { url } }` shape `schemaImages` expects —
    // the same shape `seo.ts` feeds to `sanityOgImageUrl` for the OG tag.
    const images = post.coverImage
      ? schemaImages(post.coverImage as Parameters<typeof schemaImages>[0])
      : FALLBACK_SCHEMA_IMAGES;
    const faq = (post.faq ?? []).filter((item) => item?.question && item?.answer);
    const jsonLd = [
      breadcrumb(crumbs),
      article({
        headline: post.title,
        description: post.excerpt,
        url: `${SITE_URL}/blog/${slug}`,
        image: images,
        datePublished: post.publishedAt ?? post._createdAt,
        dateModified: post._updatedAt,
        authorName: post.author,
        articleSection: post.category,
        wordCount: portableTextWordCount(post.body),
        keywords: post.keywords,
        type: "BlogPosting",
      }),
      // The FAQ is not rendered separately: posts already carry their Q&As in a visible
      // FAQ section at the end of `body`, so the markup's content is on the page (Google's
      // requirement) and a second rendering would duplicate it. This is an editorial
      // invariant, not an enforced one — an `faq` entry with no counterpart in `body`
      // emits markup for content that is not on the page, which Google's structured-data
      // policy forbids.
      ...(faq.length ? [faqPage(faq.map((item) => ({ q: item.question, a: item.answer })))] : []),
    ];
    return seo({
      title: post.title,
      description: post.excerpt ?? "",
      path,
      type: "article",
      noindex: !hasBody,
      publishedTime: post.publishedAt ?? post._createdAt,
      modifiedTime: post._updatedAt,
      authorName: post.author,
      sanityMeta: post as Parameters<typeof seo>[0]["sanityMeta"],
      jsonLd,
    });
  },
  notFoundComponent: NotFoundPage,
  component: BlogPostPage,
});

function BlogPostPage() {
  const post = Route.useLoaderData() as BlogPost;
  const { slug } = Route.useParams();
  const hasBody = Array.isArray(post.body) && post.body.length > 0;
  const imgUrl = post.coverImage
    ? urlFor(post.coverImage as Parameters<typeof urlFor>[0])
        .width(1600)
        .height(900)
        .fit("crop")
        .auto("format")
        .url()
    : null;

  return (
    <article className="max-w-3xl mx-auto px-6 py-16 text-white">
      <nav className="text-sm text-white/50 mb-8 flex items-center gap-2">
        <Link to="/" className="hover:text-white transition-colors">
          Home
        </Link>
        <span aria-hidden>›</span>
        <Link to="/blog" search={{ page: 1 }} className="hover:text-white transition-colors">
          Blog
        </Link>
        <span aria-hidden>›</span>
        <span className="text-white/80 truncate">{post.title}</span>
      </nav>

      {post.category && (
        <p className="text-[10px] font-medium uppercase tracking-[0.22em] text-pink-soft mb-4">
          {post.category}
        </p>
      )}

      <h1 className="text-4xl md:text-5xl font-semibold tracking-tight text-white mb-6 leading-tight">
        {post.title}
      </h1>

      <div className="flex items-center gap-3 text-sm text-white/50 mb-8">
        {post.author && <span>{post.author}</span>}
        {post.author && post.publishedAt && <span aria-hidden>·</span>}
        {post.publishedAt && (
          <time dateTime={post.publishedAt}>
            {new Date(post.publishedAt).toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </time>
        )}
        {post.readTime && (
          <>
            <span aria-hidden>·</span>
            <span>{post.readTime}</span>
          </>
        )}
      </div>

      {imgUrl && (
        <img
          src={imgUrl}
          alt={post.title}
          className="w-full rounded-lg object-cover mb-10 aspect-video"
          loading="eager"
        />
      )}

      {hasBody ? (
        <PortableText value={post.body} className="space-y-0" />
      ) : (
        <div className="space-y-6">
          {post.excerpt && <p className="text-lg text-white/75 leading-relaxed">{post.excerpt}</p>}
          {post.externalUrl && (
            <a
              href={post.externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-pink-soft hover:text-white transition-colors font-medium"
            >
              Read the full post →
            </a>
          )}
        </div>
      )}

      <div className="mt-16 pt-8 border-t border-white/10">
        <Link
          to="/blog"
          search={{ page: 1 }}
          className="text-sm text-pink-soft hover:text-white transition-colors"
        >
          ← Back to Blog
        </Link>
      </div>
    </article>
  );
}
